import { formatPhoneNumber } from "react-phone-number-input";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ProfileTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import {
  UnencryptedStorageKey,
  useUnencryptedStorage,
} from "@/hooks/useUnencryptedStorage";
import { useAnalytics } from "@/services/Analytics";
import { Avatar, Button, FAIconName, LabelWithIcon, Text } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs, listWithParentheses } from "@/utils/string.util";

enum AnalyticsSource {
  Onboarding = "(onboarding)/verify-info",
  ProfileTab = "(tabs)/profile",
}

const MissingFieldText = "Not set";

const isMissing = (text?: string) =>
  isNotNilNotEmpty(text) && text === MissingFieldText;

interface UserOverviewCardProps {
  address: string;
  analyticsSource: AnalyticsSource;
  contactPreferences: string;
  fullName: string;
  gender: string;
  isAdditionalConsent?: boolean;
  languages: string;
  phoneNumber: string;
  pronouns?: string[];
  selectedUserId: string;
  shouldDisplayMissingFields?: boolean;
  userType: BaseUserType;
}

const UserOverviewCard = ({
  address,
  analyticsSource,
  contactPreferences,
  fullName,
  gender,
  isAdditionalConsent = false,
  languages,
  phoneNumber,
  pronouns,
  selectedUserId,
  shouldDisplayMissingFields = true,
  userType,
}: UserOverviewCardProps) => {
  const { track } = useAnalytics();
  const baseTestID = joinTestIDs(selectedUserId, ProfileTestID.UserOverview);
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber) || phoneNumber;
  const { deleteUnencryptedValue } = useUnencryptedStorage();

  const handleButtonPress = async () => {
    track(ProfileAnalyticsEvent.EditProfile, {
      [AnalyticsFieldName.Component]: UserOverviewCard.name,
      [AnalyticsFieldName.Source]: analyticsSource,
      ...(selectedUserId && {
        [AnalyticsFieldName.SelectedUser]: selectedUserId,
      }),
    });

    if (!isNotNilNotEmpty(selectedUserId)) {
      return;
    }
    if (isAdditionalConsent) {
      router.replace({
        params: { selectedUserId },
        pathname: ModalPath.EditProfile,
      });
      await deleteUnencryptedValue(UnencryptedStorageKey.UserDismissedConsent);
    } else {
      router.navigate({
        params: { selectedUserId },
        pathname: ModalPath.EditProfile,
      });
    }
  };
  const handleMissing = (text?: string) => {
    if (!shouldDisplayMissingFields) {
      return text;
    }
    const isEmptyText = !isNotNilNotEmpty(text?.trim());
    return isEmptyText ? MissingFieldText : text;
  };
  const displayPhoneNumber = handleMissing(formattedPhoneNumber);
  const displayAddress = handleMissing(address);
  const displayContactPreferences = handleMissing(contactPreferences);
  const displayGender = handleMissing(gender);
  const displayLanguages = handleMissing(languages);

  return (
    <BaseCard testID={joinTestIDs(baseTestID, "card")}>
      <VStack gap={12} testID={joinTestIDs(baseTestID, "card-contents")}>
        <HStack
          alignItems="center"
          justifyContent="space-between"
          testID={joinTestIDs(baseTestID, "header")}
        >
          <Avatar
            size="md"
            testID={baseTestID}
            userName={fullName}
            userType={userType}
          />
          <Button
            leftIcon={FAIconName.Pen}
            onPress={handleButtonPress}
            size="small"
            testID={joinTestIDs(baseTestID, "edit-profile")}
            title="Edit profile"
            variant="secondary"
          />
        </HStack>
        <VStack
          gap={2}
          testID={joinTestIDs(baseTestID, "full-name-and-pronouns")}
        >
          <Text testID={joinTestIDs(baseTestID, "full-name")} variant="h5">
            {fullName}
          </Text>
          {isNotNilNotEmpty(pronouns) && (
            <Text testID={joinTestIDs(baseTestID, "pronouns")}>
              {listWithParentheses(pronouns)}
            </Text>
          )}
        </VStack>
        <VStack gap={8} testID={joinTestIDs(baseTestID, "user-details")}>
          <LabelWithIcon
            iconName={FAIconName.Mobile}
            isDisabled={isMissing(displayPhoneNumber)}
            testID={joinTestIDs(baseTestID, "phone-number")}
            text={displayPhoneNumber}
          />
          <LabelWithIcon
            iconName={FAIconName.LocationDot}
            isDisabled={isMissing(displayAddress)}
            testID={joinTestIDs(baseTestID, "address")}
            text={displayAddress}
          />
          <LabelWithIcon
            iconName={FAIconName.MessageCheck}
            isDisabled={isMissing(displayContactPreferences)}
            testID={joinTestIDs(baseTestID, "contact-preferences")}
            text={displayContactPreferences}
          />
          <LabelWithIcon
            iconName={FAIconName.UserGroupSimple}
            isDisabled={isMissing(displayGender)}
            testID={joinTestIDs(baseTestID, "gender")}
            text={displayGender}
          />
          <LabelWithIcon
            iconName={FAIconName.Globe}
            isDisabled={isMissing(displayLanguages)}
            testID={joinTestIDs(baseTestID, "languages")}
            text={displayLanguages}
          />
        </VStack>
      </VStack>
    </BaseCard>
  );
};

export default UserOverviewCard;
export { AnalyticsSource, MissingFieldText };
export type { UserOverviewCardProps };
